import React, { useState, useRef, useEffect } from "react";
import { Bot, Plus, Maximize2, Minimize2, ChevronLeft, MoreVertical, Trash2 } from "lucide-react";
import SearchBar from "../SearchBar/SearchBar.js";
import "./AgentSidebar.css";
import Button from "../Button/Button.js";

import SkeletonLoader from "../SkeletonLoader/SkeletonLoader.js";

export default function AgentSidebar({
  agents = [],
  selectedAgent,
  loading,
  searchQuery,
  setSearchQuery,
  isFullscreen,
  setIsFullscreen,
  setShowCreateModal,
  handleSelectAgent,
  handleDeleteAgent,
  dirWidth,
  isDirCollapsed,
  setIsDirCollapsed,
}) {
  const [activeMenuId, setActiveMenuOpenId] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setActiveMenuOpenId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const safeQuery = String(searchQuery || "").toLowerCase();
  const filteredAgents = (Array.isArray(agents) ? agents : []).filter((a) => {
    if (!a) return false;
    const name = String(a.campaign_name || a.name || a.title || "Unnamed Agent").toLowerCase();
    return name.includes(safeQuery);
  });
  

  return (
    <aside className="studio-directory-panel" style={{ width: dirWidth }}>
      <div className="directory-header">
        <div className="directory-title-row">
          <h2 className="directory-title">Your Agents</h2>
          <div style={{ display: "flex", gap: 6 }}>
            <Button
              variant="ghost"
              size="sm"
              className="btn-import-agent"
              title="Collapse Sidebar"
              onClick={() => setIsDirCollapsed(true)}
              style={{ width: "32px", height: "32px", padding: 0 }}
            >
              <ChevronLeft size={14} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="btn-import-agent"
              title={isFullscreen ? "Minimize Window" : "Maximize Full Window"}
              onClick={() => setIsFullscreen(!isFullscreen)}
              style={{ width: "32px", height: "32px", padding: 0, backgroundColor: isFullscreen ? "var(--slate-200)" : "" }}
            >
              {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            </Button>
          </div>
        </div>
        <div className="directory-actions-row">
          <Button variant="outline" className="btn-new-agent" onClick={() => setShowCreateModal(true)}>
            <Plus size={16} /> New Agent
          </Button>
        </div>
        <div className="directory-search-box" style={{ marginTop: 10 }}>
          <SearchBar
            placeholder="Search agents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            width="100%"
          />
        </div>
      </div>

      <div className="directory-list-container">
        {loading ? (
          <div style={{ padding: "8px 12px" }}>
            <SkeletonLoader type="list" count={5} />
          </div>
        ) : filteredAgents.length === 0 ? (
          <div style={{ textAlign: "center", padding: 24, color: "var(--slate-400)", fontSize: 13 }}>
            No agents found
          </div>
        ) : (
          filteredAgents.map((a) => {
            const isSelected = selectedAgent?.id === a.id;
            const isMenuOpen = activeMenuId === a.id;

            return (
              <div
                key={a.id}
                className={`agent-list-item ${isSelected ? "active" : ""}`}
                onClick={() => handleSelectAgent(a)}
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "10px 12px",
                  borderRadius: "10px",
                  cursor: "pointer",
                  transition: "all 0.15s ease"
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, minWidth: 0 }}>
                  <div className="agent-item-avatar" style={{ flexShrink: 0 }}>
                    <Bot size={16} />
                  </div>
                  <div className="agent-item-info" style={{ flex: 1, minWidth: 0 }}>
                    <div className="agent-item-name" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {a.campaign_name || a.name || "Unnamed Agent"}
                    </div>
                    <div className="agent-item-meta" style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                      {a.total_leads !== undefined ? (
                        <>
                          <span 
                            className="status-indicator-dot" 
                            style={{ 
                              width: 6, 
                              height: 6, 
                              borderRadius: "50%", 
                              backgroundColor: String(a.status).toLowerCase() === "active" || String(a.status).toLowerCase() === "start" ? "var(--success)" : "var(--slate-400)",
                              flexShrink: 0
                            }} 
                          />
                          <span style={{ 
                            color: String(a.status).toLowerCase() === "active" || String(a.status).toLowerCase() === "start" ? "var(--success)" : "var(--slate-400)",
                            fontWeight: 600
                          }}>
                            {a.status}
                          </span>
                          <span>•</span>
                          <span>{a.total_leads} Leads</span>
                        </>
                      ) : (
                        <>
                          <span>{a.status}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Three Dots Action Menu Button */}
                <div 
                  style={{ position: "relative", flexShrink: 0, marginLeft: "6px" }}
                  ref={isMenuOpen ? menuRef : null}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "28px",
                      height: "28px",
                      borderRadius: "6px",
                      border: "none",
                      backgroundColor: isMenuOpen ? "var(--slate-200, #e2e8f0)" : "transparent",
                      color: isSelected ? "var(--slate-800, #1e293b)" : "var(--slate-500, #64748b)",
                      cursor: "pointer",
                      transition: "all 0.15s ease"
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveMenuOpenId(isMenuOpen ? null : a.id);
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(0,0,0,0.06)")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = isMenuOpen ? "var(--slate-200, #e2e8f0)" : "transparent")}
                    title="Agent Actions"
                  >
                    <MoreVertical size={15} />
                  </button>

                  {isMenuOpen && (
                    <div
                      style={{
                        position: "absolute",
                        top: "calc(100% + 4px)",
                        right: 0,
                        minWidth: "140px",
                        backgroundColor: "var(--white, #ffffff)",
                        border: "1px solid var(--gray-200, #e2e8f0)",
                        borderRadius: "8px",
                        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
                        padding: "4px",
                        zIndex: 1000
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          width: "100%",
                          padding: "7px 10px",
                          border: "none",
                          borderRadius: "6px",
                          backgroundColor: "transparent",
                          color: "var(--error, #e63946)",
                          fontSize: "12.5px",
                          fontWeight: 600,
                          cursor: "pointer",
                          textAlign: "left",
                          transition: "background-color 0.15s ease"
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--secondary-light, #fdf2f2)")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveMenuOpenId(null);
                          if (handleDeleteAgent) {
                            handleDeleteAgent(a);
                          }
                        }}
                      >
                        <Trash2 size={13} /> Delete Agent
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </aside>
  );
}
